/**
 * @file API 回應處理器
 * @description 統一處理 API 回應，包含狀態碼判斷、錯誤處理、Token 刷新機制
 */
import Swal from 'sweetalert2';
import localStorageUtil from 'utils/localStorageUtil';
import LocalStorageKeys from 'constants/localStorageKeys';
import type { FetchOptions } from './fetch';

/** Token 刷新狀態標記 */
let isRefreshing = false;

/** 請求佇列項目介面 */
interface QueueItem {
  originalUrl: string;
  originalOptions: FetchOptions;
  resolve: (value?: unknown) => void;
  reject: (error: Error) => void;
}

/** Token 過期時的請求佇列 */
const failedQueue: QueueItem[] = [];

/** API 回應結果介面 */
interface ApiResult {
  msg?: string;
  message?: string;
  accessToken?: string;
  refreshToken?: string;
}

/** API JSON 回應介面 */
interface ApiJsonResponse {
  statusCode?: number;
  result?: ApiResult;
}

/**
 * 處理 API Response
 * @param res - fetch Response 物件
 * @param type - 回應類型 ('json' | 'blob')
 * @param originalUrl - 原始請求 URL
 * @param originalOptions - 原始請求選項
 * @returns Promise 回傳處理後的資料
 * @description 根據 statusCode 處理不同情況，包含 Token 刷新機制
 */
const apiResponseHandler = async (
  res: Response,
  type: 'json' | 'blob' = 'json',
  originalUrl: string,
  originalOptions: FetchOptions
): Promise<unknown> => {
  try {
    if (type === 'json') {
      const response: ApiJsonResponse = await res.json();
      let msg = '';

      if (res?.ok) {
        switch (response?.statusCode) {
          case 1000:
          case 220005:
          case 220007:
          case 360005:
          case 360006:
          case 360007:
          case 320025:
          case 320026:
          case 320027:
          case 320028:
          case 320029:
          case 320030:
          case 320031:
          case 320032:
          case 320033:
          case 320034:
          case 320035:
          case 320036:
            return response.result;
          case 400001:
          case 400002:
          case 400003:
          case 400004:
          case 400005:
          case 400006:
          case 410001:
          case 410002:
          case 410003:
          case 410004:
          case 410005:
          case 410006:
          case 410007:
          case 410008:
          case 410009:
          case 410010:
          case 410011:
          case 420001:
            msg = response?.result?.msg || response.result?.message || '';
            errorFire(msg, response?.statusCode);
            return false;
          default:
            msg = response?.result?.msg || response.result?.message || '';
            errorFire(msg, response?.statusCode);
            return null;
        }
      } else if (res?.status === 403) {
        switch (response?.statusCode) {
          case 200008:
            msg = response?.result?.msg || response.result?.message || '';
            errorFire(msg, response?.statusCode).then(() => {
              localStorageUtil.removeItem(LocalStorageKeys.UserInfo);
              window.location.href = '/login';
            });
            return;
          case 200009:
            return await handleTokenTimeOut(response, originalUrl, originalOptions);
          default:
            errorFire(response?.result?.msg || '', response?.statusCode);
        }
      } else {
        errorFire(response?.result?.msg || '', response?.statusCode);
      }
    } else if (type === 'blob') {
      const response = await res.blob();
      console.log(response);
      return response;
    } else {
      return res;
    }
  } catch (error) {
    errorFire(String(error));
    return null;
  }
};

/**
 * 顯示錯誤彈窗
 * @param msg - 錯誤訊息
 * @param statusCode - 狀態碼
 * @returns SweetAlert Promise
 */
function errorFire(msg: string, statusCode?: number) {
  console.log('=== errorFire msg', msg);
  const showMsg = true;

  return Swal.fire({
    icon: 'error',
    html: !statusCode
      ? showMsg
        ? msg
        : '網路不穩，請重新再試'
      : showMsg
        ? `${msg}<br>(${statusCode})`
        : `網路不穩，請重新再試<br>(${statusCode})`
  });
}

/**
 * 處理請求佇列
 * @param error - 錯誤物件 (若有)
 * @param token - 新的 access token (若有)
 * @description Token 刷新後重新執行佇列中的請求
 */
const processQueue = (error: Error | null = null, token: string | null = null): void => {
  failedQueue.forEach((queueItem) => {
    if (error) {
      queueItem.reject(error);
    } else if (token) {
      // Lazy import to avoid circular dependency
      import('./fetch').then(({ default: fetchJson }) => {
        fetchJson(queueItem.originalUrl, queueItem.originalOptions)
          .then(queueItem.resolve)
          .catch(queueItem.reject);
      });
    } else {
      queueItem.resolve();
    }
  });
  failedQueue.length = 0;
};

/**
 * 處理 Token 過期
 * @param res - API 回應
 * @param originalUrl - 原始請求 URL
 * @param originalOptions - 原始請求選項
 * @returns Promise 回傳重新執行的請求結果
 * @description 自動刷新 Token 並重新執行原始請求
 */
async function handleTokenTimeOut(
  res: ApiJsonResponse,
  originalUrl: string,
  originalOptions: FetchOptions
): Promise<unknown> {
  const userInfo = localStorageUtil.getItem(LocalStorageKeys.UserInfo) as {
    accessToken?: string;
    refreshToken?: string;
  } | null;

  localStorageUtil.setItem(LocalStorageKeys.UserInfo, {
    ...userInfo,
    accessToken: ''
  });

  if (!isRefreshing) {
    isRefreshing = true;
    // Lazy import to avoid circular dependency
    const api = await import('services/api').then((m) => m.default);
    const resData = await api.auth.refresh();
    isRefreshing = false;

    if (resData && resData?.accessToken && resData?.refreshToken) {
      const REFRESH_TOKEN_TTL = 48 * 60 * 60 * 1000; // 48hr
      localStorageUtil.setItem(LocalStorageKeys.UserInfo, {
        ...userInfo,
        accessToken: resData?.accessToken,
        refreshToken: resData?.refreshToken
      }, REFRESH_TOKEN_TTL);

      failedQueue.push({
        originalUrl,
        originalOptions,
        resolve: (response) => response,
        reject: (error) => {
          throw error;
        }
      });
      processQueue(null, resData?.accessToken);

      const fetchJson = await import('./fetch').then((m) => m.default);
      return fetchJson(originalUrl, originalOptions);
    } else {
      processQueue();
      const msg = res?.result?.msg || res.result?.message || '';
      errorFire(msg, res?.statusCode).then(() => {
        localStorageUtil.removeItem(LocalStorageKeys.UserInfo);
        window.location.href = '/login';
      });
    }
  } else {
    return new Promise((resolve, reject) => {
      failedQueue.push({
        originalUrl,
        originalOptions,
        resolve,
        reject
      });
    });
  }
}

export default apiResponseHandler;
